import React from "react";

import { css, cx } from "@emotion/css";
import type { Theme } from "@mui/material/styles";

import { useStyles } from "@/hooks/useStyles.ts";
import { type Option, ChoiceItem } from "@/uiKit/atoms/inputs/ChoiceItem.tsx";
import { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";
import { suffixId } from "@/uiKit/atoms/utils/ids.ts";

export type BaseChoiceInputProps<T> = {
  value: T | undefined;
  choices: Option<T>[];
  onChange: (value: T | undefined, event: React.MouseEvent<HTMLElement>) => void;
  onBlur?: (value: T | undefined, event: React.FocusEvent<HTMLElement>) => void;
  toggleOnClick?: boolean;
  disabled?: boolean;
  error?: boolean;
  /** Prefix of each option's id (`${idPrefix}-${option.value}`); defaults to `id`. */
  idPrefix?: string;
};

export type ChoiceInputProps<T> = AtomProps<BaseChoiceInputProps<T>, StyleClasses<"root">>;

export const ChoiceInput = <T,>({
  id,
  idPrefix = id,
  value,
  choices,
  onChange,
  onBlur,
  toggleOnClick,
  disabled,
  error,
  className,
  classes,
  ...props
}: ChoiceInputProps<T>): React.ReactElement => {
  const styles = useStyles(makeStyles);

  return (
    <div id={id} className={cx(styles.root, className)} {...props}>
      {choices.map(option => (
        <ChoiceItem
          key={`${option.value}`}
          id={suffixId(idPrefix, `${option.value}`)}
          option={option}
          selected={option.value === value}
          onClick={event =>
            toggleOnClick && option.value === value ? onChange(undefined, event) : onChange(option.value, event)
          }
          onBlur={event => onBlur?.(option.value, event)}
          disabled={disabled}
          color={error ? "error" : undefined}
        />
      ))}
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  root: css({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(8),
  }),
});
